import axiosInstance from './axiosCreator';
import { DailyDueMode, DailyNewMode, User } from '../types/User';
import { School } from '../types/School';
import axios from 'axios';
import {
  schoolLoading,
  schoolReceived,
  schoolFailed,
} from '../slices/school/schoolSlice';
import { store } from '../store/store';
import { getProfilePictureUploadUrl } from './aws';
import {
  examUpdated,
  examsSelectors,
  setPrimaryExam,
} from '../slices/exams/examsSlice';
import { fetchExams } from './exams';
import { userUpdateReceived } from '../slices/user/userSlice';
import publicApi from './publicApi';

// Update user exam
export const updateUserExamBE = async ({
  userId,
  examId,
  name,
  schoolId,
  primary,
  examDate,
  selectedFlashcardCount,
  selectedVignetteCount,
  isUsingFlashcards,
  isUsingQbank,
  isUsingVideos,
  hasCompletedRealExam,
  realExamScore,
  ignoredExamDate,
  clearTodaySession,
  lectureMode,
  dailyFlashcardsNewMode,
  dailyFlashcardsNewFixedCount,
  dailyFlashcardsDueMode,
  dailyFlashcardsDueFixedCap,
  dailyQbankNewMode,
  dailyQbankNewFixedCount,
  dailyQbankDueMode,
  dailyQbankDueFixedCap,
  dailyVideosNewMode,
  dailyVideosNewFixedCount,
  applyDailySessionSizeToAllStandardExams,
}: {
  userId: string;
  examId: string;
  name?: string | null;
  examDate?: string | null;
  primary?: boolean;
  selectedFlashcardCount?: number;
  selectedVignetteCount?: number;
  isUsingFlashcards?: boolean;
  isUsingQbank?: boolean;
  isUsingVideos?: boolean;
  hasCompletedRealExam?: boolean;
  realExamScore?: number | null;
  ignoredExamDate?: boolean;
  clearTodaySession?: boolean;
  schoolId?: string | null;
  lectureMode?: 'AUTOPILOT' | 'MANUAL';
  dailyFlashcardsNewMode?: DailyNewMode;
  dailyFlashcardsNewFixedCount?: number;
  dailyFlashcardsDueMode?: DailyDueMode;
  dailyFlashcardsDueFixedCap?: number;
  dailyQbankNewMode?: DailyNewMode;
  dailyQbankNewFixedCount?: number;
  dailyQbankDueMode?: DailyDueMode;
  dailyQbankDueFixedCap?: number;
  dailyVideosNewMode?: DailyNewMode;
  dailyVideosNewFixedCount?: number;
  applyDailySessionSizeToAllStandardExams?: boolean;
}) => {
  try {
    const hasDailySessionSizeUpdate =
      dailyFlashcardsNewMode !== undefined ||
      dailyFlashcardsNewFixedCount !== undefined ||
      dailyFlashcardsDueMode !== undefined ||
      dailyFlashcardsDueFixedCap !== undefined ||
      dailyQbankNewMode !== undefined ||
      dailyQbankNewFixedCount !== undefined ||
      dailyQbankDueMode !== undefined ||
      dailyQbankDueFixedCap !== undefined ||
      dailyVideosNewMode !== undefined ||
      dailyVideosNewFixedCount !== undefined;

    const params = {
      ...(examDate !== undefined && { examDate }),
      ...(name !== undefined && name !== null && { name }),
      ...(schoolId !== undefined && { schoolId }),
      ...(primary !== undefined && { primary }),
      ...(selectedFlashcardCount !== undefined && { selectedFlashcardCount }),
      ...(selectedVignetteCount !== undefined && { selectedVignetteCount }),
      ...(isUsingFlashcards !== undefined && { isUsingFlashcards }),
      ...(isUsingQbank !== undefined && { isUsingQbank }),
      ...(isUsingVideos !== undefined && { isUsingVideos }),
      ...(hasCompletedRealExam !== undefined && { hasCompletedRealExam }),
      ...(realExamScore !== undefined && { realExamScore }),
      ...(ignoredExamDate !== undefined && { ignoredExamDate }),
      ...(clearTodaySession !== undefined && { clearTodaySession }),
      ...(lectureMode !== undefined && { lectureMode }),
      ...(dailyFlashcardsNewMode !== undefined && { dailyFlashcardsNewMode }),
      ...(dailyFlashcardsNewFixedCount !== undefined && {
        dailyFlashcardsNewFixedCount,
      }),
      ...(dailyFlashcardsDueMode !== undefined && { dailyFlashcardsDueMode }),
      ...(dailyFlashcardsDueFixedCap !== undefined && {
        dailyFlashcardsDueFixedCap,
      }),
      ...(dailyQbankNewMode !== undefined && { dailyQbankNewMode }),
      ...(dailyQbankNewFixedCount !== undefined && { dailyQbankNewFixedCount }),
      ...(dailyQbankDueMode !== undefined && { dailyQbankDueMode }),
      ...(dailyQbankDueFixedCap !== undefined && { dailyQbankDueFixedCap }),
      ...(dailyVideosNewMode !== undefined && { dailyVideosNewMode }),
      ...(dailyVideosNewFixedCount !== undefined && {
        dailyVideosNewFixedCount,
      }),
      ...(applyDailySessionSizeToAllStandardExams !== undefined && {
        applyDailySessionSizeToAllStandardExams,
      }),
    };

    const response = await axiosInstance.patch(
      `/users/${userId}/exams/${examId}`,
      params
    );

    if (!response.data.success) {
      throw new Error('Failed to update user exam');
    }

    const updatedExam = response.data.data;

    if (updatedExam?.id) {
      store.dispatch(examUpdated(updatedExam));

      if (primary === true && updatedExam.type === 'CUSTOM') {
        const preclinicalExam = examsSelectors.selectPreclinicalExam(
          store.getState() as never
        );

        if (preclinicalExam?.id) {
          store.dispatch(setPrimaryExam(preclinicalExam.id));
        }
      }
    } else {
      store.dispatch(examUpdated({ id: examId, ...params }));
    }

    if (applyDailySessionSizeToAllStandardExams !== undefined) {
      store.dispatch(
        userUpdateReceived({ applyDailySessionSizeToAllStandardExams })
      );
    }

    if (hasDailySessionSizeUpdate && applyDailySessionSizeToAllStandardExams) {
      await fetchExams();
    }

    return updatedExam;
  } catch (error) {
    // Rethrow: the only caller is `useUserExamUpdate`, whose catch is what
    // reports a failed update (its `onError`) and suppresses the success
    // snackbar. Swallowing here left every failure invisible — a rejected
    // exam-date save blanked the field and still said nothing (ORA-498).
    console.error(error);
    throw error;
  }
};

export const getAllBetaUsers = async () => {
  try {
    const response = await axiosInstance.get('/users/allowedBetaUsers');
    return response.data;
  } catch (error) {
    // Handle error
    console.error(error);
  }
};

// Get batched users
export const getBatchedUsers = async (
  skip: number,
  take: number,
  hideSynaptiq: boolean,
  email: string = ''
) => {
  try {
    const response = await axiosInstance.get(
      `/users/paginated?skip=${skip}&take=${take}&hideSynaptiq=${hideSynaptiq}&email=${email}`
    );

    return response.data;
  } catch (error) {
    // Handle error
    console.error(error);
  }
};

export const getAdminAndQaUsers = async () => {
  try {
    const response = await axiosInstance.get('/users/admin-role-users');
    return response.data;
  } catch (error) {
    console.error(error);
  }
};

// Get all free_access holders (including lapsed ones) for the admin Roles
// dashboard read-only table. See ORA-210.
export const getFreeAccessUsers = async () => {
  try {
    const response = await axiosInstance.get('/users/free-access-users');
    return response.data;
  } catch (error) {
    console.error(error);
  }
};

// Update a user
export const updateUser = async (id: string, userData: Partial<User>) => {
  try {
    const response = await axiosInstance.put(`/users/${id}`, userData);
    return response.data;
  } catch (error) {
    // Handle error
    console.error(error);
  }
};

// Update user referralId
export const updateUserReferralId = async (id: string, referralId: string) => {
  try {
    const response = await axiosInstance.put(`/users/${id}/referralId`, {
      referralId,
    });

    return response.data;
  } catch (error) {
    // Handle error
    console.error(error);
  }
};

// Delete a user
export const deleteUser = async (id: string) => {
  try {
    const response = await axiosInstance.delete(`/users/${id}`);
    return response;
  } catch (error) {
    // Handle error
    console.error(error);
  }
};

// Get Mobile App Info
export const getMobileAppInfo = async () => {
  try {
    const response = await axiosInstance.get('/users/mobileAppInfo');
    return response.data;
  } catch (error) {
    // Handle error
    console.error(error);
  }
};

// Get Ambassadors
export const getAmbassadors = async () => {
  try {
    const response = await axiosInstance.get('/users/ambassadors');
    return response.data;
  } catch (error) {
    console.error('Error getting ambassadors:', error);
  }
};

/**
 * Updates a user's ambassador status and related fields
 * @param userId The ID of the user to update
 * @param ambassador Boolean indicating whether the user is an ambassador
 * @param ambassadorStartDate Optional ISO date string for when the user became an ambassador
 * @returns The updated user object or null if the request failed
 */
export const updateUserAmbassadorStatus = async (
  userId: string,
  ambassador: boolean,
  ambassadorStartDate?: string
): Promise<User | null> => {
  try {
    const response = await axiosInstance.put(`/users/${userId}/ambassador`, {
      ambassador,
      ...(ambassadorStartDate && { ambassadorStartDate }),
      showAmbassadorOnboarding: false,
    });

    return response.data;
  } catch (error) {
    console.error('Error updating user ambassador status:', error);
    return null;
  }
};

export const uploadProfilePictureToS3 = async (
  file: File,
  userId: string
): Promise<string> => {
  const bucket = 'ora-imgs';
  const dirName = `avatars/${userId}`;
  const data = await getProfilePictureUploadUrl(bucket, dirName, file.name);

  if (!userId) return '';

  if (data.url) {
    try {
      await axios.put(data.url, file, {
        headers: {
          'Content-Type': file.type,
        },
      });

      return `https://ora-imgs.s3.us-west-1.amazonaws.com/${dirName}/${file.name}`;
    } catch (error) {
      console.error('Upload error:', error);
      throw new Error(
        `Upload failed: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    }
  }

  throw new Error('Failed to get presigned URL');
};

/**
 * Deletes a profile picture from S3 using the profile picture URL
 * @param profilePictureUrl The full URL to the profile picture to delete
 * @returns The response from the delete API call or null if extraction failed
 */
export const deleteProfilePictureFromS3 = async (profilePictureUrl: string) => {
  if (!profilePictureUrl || !profilePictureUrl.includes('ora-imgs')) {
    return null;
  }

  try {
    // Extract the profile picture information from the URL
    const pictureInfo = extractProfilePictureInfo(profilePictureUrl);

    if (!pictureInfo) {
      throw new Error('Could not extract profile picture information from URL');
    }

    // Call the profile-picture delete endpoint with extracted parameters
    const response = await axiosInstance.delete(
      `/aws/profile-picture?bucket=${pictureInfo.bucket}&directory=${pictureInfo.directory}&filename=${pictureInfo.filename}`
    );

    return response.data;
  } catch (error) {
    console.error('Error deleting profile picture:', error);
    throw new Error(
      `Delete failed: ${error instanceof Error ? error.message : 'Unknown error'}`
    );
  }
};

/**
 * @private Internal interface for profile picture parameters
 */
interface ProfilePictureParams {
  bucket: string;
  directory: string;
  filename: string;
}

/**
 * @private Extracts profile picture information from a user's profile picture URL
 * @param profilePictureUrl The full URL to the profile picture
 * @returns Object containing bucket, directory, and filename
 */
const extractProfilePictureInfo = (
  profilePictureUrl: string
): ProfilePictureParams | null => {
  try {
    // Expected format: https://ora-imgs.s3.us-west-1.amazonaws.com/avatars/userId/filename.jpg
    const bucket = 'ora-imgs';
    // Extract the path after the domain
    const urlObj = new URL(profilePictureUrl);
    const pathParts = urlObj.pathname.split('/').filter(Boolean);

    // The first part should be 'avatars'
    if (pathParts.length < 2 || pathParts[0] !== 'avatars') {
      return null;
    }

    // The last part is the filename
    const filename = pathParts[pathParts.length - 1];
    // The directory is everything between the bucket and filename
    const directory = pathParts.slice(0, pathParts.length - 1).join('/');

    return {
      bucket,
      directory,
      filename,
    };
  } catch (error) {
    console.error('Error extracting profile picture info:', error);
    return null;
  }
};

// Get a user's school (must be the current user)
export const getUserSchool = async (
  userId: string
): Promise<School | null | undefined> => {
  try {
    store.dispatch(schoolLoading());
    const response = await axiosInstance.get(`/users/${userId}/school`);
    if (response.data.success) {
      store.dispatch(schoolReceived(response.data.data));
      return response.data.data;
    } else {
      store.dispatch(
        schoolFailed(response.data.error || 'Unknown backend error')
      );

      console.error(
        'Failed to fetch user school:',
        response.data.error || 'Unknown backend error'
      );

      return null;
    }
  } catch (error) {
    console.error('Error fetching user school:', error);
    if (axios.isAxiosError(error) && error.response) {
      console.error('Status:', error.response.status);
      console.error('Data:', error.response.data);
      if (error.response.status === 403) {
        return null;
      }
    }
    return undefined;
  }
};

export const updateUserSchool = async (
  userId: string,
  schoolId: string
): Promise<School | null> => {
  try {
    store.dispatch(schoolLoading());

    const response = await axiosInstance.put(`/users/${userId}/school`, {
      schoolId,
    });

    if (response.data.success) {
      store.dispatch(schoolReceived(response.data.data));
      fetchExams();
      return response.data.data;
    }

    store.dispatch(
      schoolFailed(response.data.error || 'Unknown backend error')
    );

    return null;
  } catch (error) {
    console.error('Error updating user school:', error);
    if (axios.isAxiosError(error) && error.response) {
      if (error.response.status === 403) {
        console.error('Forbidden: You can only update your own school.');
      } else if (error.response.status === 404) {
        console.error('School not found');
      }
    }
    return null;
  }
};

/**
 * Completes email verification using the provided token
 * @param token - The verification token received via email
 * @returns Promise with success status and any error information
 */
export const completeEmailVerification = async (token: string) => {
  try {
    const response = await publicApi.post('/users/completeEmailVerification', {
      token,
    });

    return response.data;
  } catch (error) {
    console.error('Error completing email verification:', error);
    throw error;
  }
};

export const sendVerificationEmail = async (
  email: string,
  examId?: string
): Promise<{ success: true }> => {
  try {
    const response = await axiosInstance.post('/users/sendVerificationEmail', {
      email,
      ...(examId ? { examId } : {}),
    });

    if (!response.data.success) {
      throw new Error(
        response.data.error || 'Unable to send the verification email.'
      );
    }

    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const message = error.response?.data?.error;
      if (typeof message === 'string' && message) {
        throw new Error(message);
      }
    }

    throw error;
  }
};

/**
 * Sends a request to become an ambassador
 * @param schoolName - Optional school name to include in the request
 * @returns Promise with success status
 */
export const requestAmbassador = async (
  schoolName?: string
): Promise<boolean> => {
  try {
    const response = await axiosInstance.post('/users/request-ambassador', {
      schoolName,
    });

    if (!response.data.success) {
      throw new Error(
        response.data.error || 'Failed to send ambassador request'
      );
    }
    return true;
  } catch (error) {
    console.error('Failed to send ambassador request:', error);
    return false;
  }
};
